import { useEffect, useRef } from 'react';

export interface OptimisticOverlayProps {
  /**
   * Cached fullName to greet. `null` means "no cache hit, render nothing".
   */
  fullName: string | null;
  /** "online" → "Anwesend", "offline" → "Offline gespeichert". */
  mode: 'online' | 'offline';
  onDismiss: () => void;
  /** Auto-dismiss after this many ms. */
  autoDismissMs?: number;
}

/**
 * In-column optimistic / offline panel rendered immediately after a scan when
 * the server hasn't responded yet (or won't, because we're offline). Replaced
 * by the real `<ScanResultOverlay>` once the server returns.
 *
 * Lives inside the kiosk middle column — not a fullscreen overlay anymore.
 */
export function OptimisticOverlay({
  fullName,
  mode,
  onDismiss,
  autoDismissMs = 2_000,
}: OptimisticOverlayProps): JSX.Element | null {
  // onDismiss in Ref kapseln (sonst startet jeder Re-Render im KioskPage —
  // z. B. der 1-Hz-Clock-Tick — den Timer neu).
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!fullName) return;
    const t = setTimeout(() => onDismissRef.current(), autoDismissMs);
    return () => clearTimeout(t);
  }, [fullName, autoDismissMs]);

  if (!fullName) return null;

  const offline = mode === 'offline';
  const firstName = fullName.split(' ')[0];
  const headline = offline ? `⏱ ${fullName}` : `Hallo, ${firstName}!`;
  const subline = offline
    ? 'Offline gespeichert — wird beim nächsten Online-Werden gesendet.'
    : 'Anwesend';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="optimistic-overlay"
      data-mode={mode}
      className="flex w-full max-w-md flex-col items-center gap-3 text-center"
    >
      <span
        className={
          offline
            ? 'inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-sm font-semibold text-muted-foreground'
            : 'inline-flex items-center gap-2 rounded-full bg-status-present/15 px-3 py-1 text-sm font-semibold text-status-present'
        }
      >
        <span
          aria-hidden
          className={`h-2 w-2 rounded-full ${offline ? 'bg-muted-foreground' : 'bg-status-present'}`}
        />
        {offline ? 'Offline gespeichert' : 'Wird verarbeitet …'}
      </span>
      <h1 className="text-4xl font-semibold leading-tight md:text-5xl">{headline}</h1>
      <p className="text-base text-muted-foreground md:text-lg">{subline}</p>
    </div>
  );
}
